<template>
  <div
    v-if="visible"
    class="calculator-layer"
    data-exam-integrity-ignore
    data-exam-selection-allowed
  >
    <section
      ref="panel"
      class="scientific-calculator"
      :style="panelStyle"
      role="dialog"
      aria-modal="false"
      aria-label="Scientific Calculator"
      tabindex="-1"
      @mousedown.stop
      @selectstart.stop
    >
      <header class="calculator-header" @mousedown.prevent="startDrag" @touchstart.prevent="startDrag">
        <div>
          <strong>Scientific Calculator</strong>
          <span>Drag to reposition</span>
        </div>
        <button type="button" class="calculator-close" aria-label="Close calculator" @mousedown.stop @click="close">
          <i class="el-icon-close" aria-hidden="true"></i>
        </button>
      </header>

      <div class="calculator-display" aria-live="polite">
        <div class="calculator-display-meta">
          <button type="button" class="angle-mode" @click="toggleAngleMode">{{ angleMode }}</button>
          <span>{{ expression || "Ready" }}</span>
        </div>
        <div class="calculator-result" :class="{ 'is-error': errorMessage }">
          {{ errorMessage || displayValue }}
        </div>
      </div>

      <div class="calculator-keypad" @click.stop>
        <button v-for="key in keys" :key="key.id" type="button" :class="keyClass(key)" @click="pressKey(key)">
          <span v-html="key.label"></span>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import {
  evaluateScientificExpression,
  formatCalculatorNumber
} from "../../utils/scientific-calculator";

const KEYS = [
  { id: "sin", label: "sin", value: "sin(", kind: "function" },
  { id: "cos", label: "cos", value: "cos(", kind: "function" },
  { id: "tan", label: "tan", value: "tan(", kind: "function" },
  { id: "ln", label: "ln", value: "ln(", kind: "function" },
  { id: "log", label: "log", value: "log(", kind: "function" },
  { id: "sqrt", label: "√", value: "sqrt(", kind: "function" },
  { id: "asin", label: "sin<sup>−1</sup>", value: "asin(", kind: "function" },
  { id: "acos", label: "cos<sup>−1</sup>", value: "acos(", kind: "function" },
  { id: "atan", label: "tan<sup>−1</sup>", value: "atan(", kind: "function" },
  { id: "pi", label: "π", value: "π", kind: "constant" },
  { id: "e", label: "e", value: "e", kind: "constant" },
  { id: "power", label: "x<sup>y</sup>", value: "^", kind: "operator" },
  { id: "open", label: "(", value: "(", kind: "open" },
  { id: "close", label: ")", value: ")", kind: "close" },
  { id: "percent", label: "%", value: "%", kind: "postfix" },
  { id: "clear", label: "AC", action: "clear", kind: "utility" },
  { id: "delete", label: "DEL", action: "delete", kind: "utility" },
  { id: "divide", label: "÷", value: "/", kind: "operator" },
  { id: "7", label: "7", value: "7", kind: "number" },
  { id: "8", label: "8", value: "8", kind: "number" },
  { id: "9", label: "9", value: "9", kind: "number" },
  { id: "square", label: "x²", value: "^2", kind: "postfix" },
  { id: "reciprocal", label: "1/x", action: "reciprocal", kind: "function" },
  { id: "multiply", label: "×", value: "*", kind: "operator" },
  { id: "4", label: "4", value: "4", kind: "number" },
  { id: "5", label: "5", value: "5", kind: "number" },
  { id: "6", label: "6", value: "6", kind: "number" },
  { id: "ans", label: "Ans", value: "ans", kind: "constant" },
  { id: "exp", label: "EXP", value: "E", kind: "exponent" },
  { id: "subtract", label: "−", value: "-", kind: "operator" },
  { id: "1", label: "1", value: "1", kind: "number" },
  { id: "2", label: "2", value: "2", kind: "number" },
  { id: "3", label: "3", value: "3", kind: "number" },
  { id: "sign", label: "±", action: "sign", kind: "utility" },
  { id: "factorial", label: "x!", value: "!", kind: "postfix" },
  { id: "add", label: "+", value: "+", kind: "operator" },
  { id: "0", label: "0", value: "0", kind: "number", wide: true },
  { id: "decimal", label: ".", value: ".", kind: "number" },
  { id: "equals", label: "=", action: "equals", kind: "equals", wide: true }
];

export default {
  name: "ScientificCalculator",
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      keys: KEYS,
      expression: "",
      displayValue: "0",
      errorMessage: "",
      angleMode: "DEG",
      ans: 0,
      justEvaluated: false,
      position: { left: null, top: null },
      dragState: null
    };
  },
  computed: {
    panelStyle() {
      if (this.position.left === null) return {};
      return {
        left: this.position.left + "px",
        top: this.position.top + "px",
        transform: "none"
      };
    }
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.$nextTick(() => {
          this.clampPosition();
          if (this.$refs.panel) this.$refs.panel.focus();
        });
      } else {
        this.stopDrag();
      }
    }
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown, true);
    window.addEventListener("resize", this.clampPosition);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown, true);
    window.removeEventListener("resize", this.clampPosition);
    this.stopDrag();
  },
  methods: {
    close() {
      this.$emit("update:visible", false);
    },
    toggleAngleMode() {
      this.angleMode = this.angleMode === "DEG" ? "RAD" : "DEG";
      this.updatePreview();
    },
    keyClass(key) {
      return [
        "calculator-key",
        "is-" + key.kind,
        { "is-wide": key.wide }
      ];
    },
    pressKey(key) {
      if (key.action) {
        this[key.action]();
        return;
      }
      this.appendToken(key.value, key.kind);
    },
    appendToken(value, kind) {
      this.errorMessage = "";
      if (this.justEvaluated && ["number", "constant", "function", "open"].indexOf(kind) !== -1) {
        this.expression = "";
      }
      this.justEvaluated = false;

      if (kind === "operator") {
        if (!this.expression && value !== "-") return;
        if (/[+\-*/^]$/.test(this.expression)) {
          if (value === "-" && !/[+\-*/^]-$/.test(this.expression)) this.expression += value;
          else this.expression = this.expression.replace(/[+\-*/^]+$/, value);
        } else {
          this.expression += value;
        }
      } else if (kind === "close") {
        const openCount = (this.expression.match(/\(/g) || []).length;
        const closeCount = (this.expression.match(/\)/g) || []).length;
        if (openCount > closeCount && !/[+\-*/^(]$/.test(this.expression)) this.expression += value;
      } else if (kind === "postfix") {
        if (this.expression && !/[+\-*/^(]$/.test(this.expression)) this.expression += value;
      } else if (kind === "exponent") {
        if (/\d(?:\.\d*)?$/.test(this.expression)) this.expression += value;
      } else {
        if (this.needsMultiplication(kind)) this.expression += "*";
        this.expression += value;
      }
      this.updatePreview();
    },
    needsMultiplication(nextKind) {
      if (!this.expression) return false;
      if (nextKind === "number" && /[eE][+-]?$/.test(this.expression)) return false;
      const endsValue = /(\d|\.|\)|π|e|ans|!|%)$/i.test(this.expression);
      if (!endsValue) return false;
      if (nextKind === "number" && /[\d.]$/.test(this.expression)) return false;
      return ["number", "constant", "function", "open"].indexOf(nextKind) !== -1;
    },
    clear() {
      this.expression = "";
      this.displayValue = "0";
      this.errorMessage = "";
      this.justEvaluated = false;
    },
    delete() {
      if (this.justEvaluated) return this.clear();
      const chunks = ["sqrt(", "asin(", "acos(", "atan(", "sin(", "cos(", "tan(", "log(", "ln(", "ans", "^2"];
      const chunk = chunks.find(item => this.expression.endsWith(item));
      this.expression = chunk
        ? this.expression.slice(0, -chunk.length)
        : this.expression.slice(0, -1);
      this.errorMessage = "";
      this.updatePreview();
    },
    sign() {
      if (!this.expression) {
        this.expression = "-";
      } else if (/^-\([\s\S]*\)$/.test(this.expression)) {
        this.expression = this.expression.slice(2, -1);
      } else {
        this.expression = "-(" + this.expression + ")";
      }
      this.justEvaluated = false;
      this.updatePreview();
    },
    reciprocal() {
      if (!this.expression) return;
      this.expression = "1/(" + this.expression + ")";
      this.justEvaluated = false;
      this.updatePreview();
    },
    updatePreview() {
      if (!this.expression || this.expression === "-") {
        this.displayValue = "0";
        return;
      }
      try {
        const value = evaluateScientificExpression(this.expression, {
          angleMode: this.angleMode,
          ans: this.ans
        });
        this.displayValue = formatCalculatorNumber(value);
      } catch (error) {
        // Incomplete expressions are normal while entering a calculation.
      }
    },
    equals() {
      try {
        const value = evaluateScientificExpression(this.expression, {
          angleMode: this.angleMode,
          ans: this.ans
        });
        this.ans = value;
        this.displayValue = formatCalculatorNumber(value);
        this.expression = this.displayValue;
        this.errorMessage = "";
        this.justEvaluated = true;
      } catch (error) {
        this.errorMessage = error.message || "Check the expression";
      }
    },
    handleKeydown(event) {
      if (!this.visible) return;
      const key = event.key;
      const directKeys = {
        "+": ["+", "operator"],
        "-": ["-", "operator"],
        "*": ["*", "operator"],
        "/": ["/", "operator"],
        "^": ["^", "operator"],
        "%": ["%", "postfix"],
        "!": ["!", "postfix"],
        "(": ["(", "open"],
        ")": [")", "close"],
        ".": [".", "number"]
      };
      if (/^\d$/.test(key)) this.appendToken(key, "number");
      else if (directKeys[key]) this.appendToken(directKeys[key][0], directKeys[key][1]);
      else if (key === "Enter" || key === "=") this.equals();
      else if (key === "Backspace") this.delete();
      else if (key === "Delete") this.clear();
      else if (key === "Escape") this.close();
      else return;
      event.preventDefault();
      event.stopPropagation();
    },
    pointerPosition(event) {
      const point = event.touches ? event.touches[0] : event;
      return { x: point.clientX, y: point.clientY };
    },
    startDrag(event) {
      const panel = this.$refs.panel;
      if (!panel) return;
      const pointer = this.pointerPosition(event);
      const rect = panel.getBoundingClientRect();
      this.position = { left: rect.left, top: rect.top };
      this.dragState = {
        offsetX: pointer.x - rect.left,
        offsetY: pointer.y - rect.top
      };
      document.addEventListener("mousemove", this.drag, true);
      document.addEventListener("mouseup", this.stopDrag, true);
      document.addEventListener("touchmove", this.drag, { passive: false, capture: true });
      document.addEventListener("touchend", this.stopDrag, true);
    },
    drag(event) {
      if (!this.dragState || !this.$refs.panel) return;
      if (event.cancelable) event.preventDefault();
      const pointer = this.pointerPosition(event);
      const rect = this.$refs.panel.getBoundingClientRect();
      const maxLeft = Math.max(8, window.innerWidth - rect.width - 8);
      const maxTop = Math.max(8, window.innerHeight - rect.height - 8);
      this.position = {
        left: Math.min(maxLeft, Math.max(8, pointer.x - this.dragState.offsetX)),
        top: Math.min(maxTop, Math.max(8, pointer.y - this.dragState.offsetY))
      };
    },
    stopDrag() {
      this.dragState = null;
      document.removeEventListener("mousemove", this.drag, true);
      document.removeEventListener("mouseup", this.stopDrag, true);
      document.removeEventListener("touchmove", this.drag, true);
      document.removeEventListener("touchend", this.stopDrag, true);
    },
    clampPosition() {
      if (!this.visible || !this.$refs.panel) return;
      const rect = this.$refs.panel.getBoundingClientRect();
      const left = this.position.left === null ? rect.left : this.position.left;
      const top = this.position.top === null ? rect.top : this.position.top;
      this.position = {
        left: Math.min(Math.max(8, window.innerWidth - rect.width - 8), Math.max(8, left)),
        top: Math.min(Math.max(8, window.innerHeight - rect.height - 8), Math.max(8, top))
      };
    }
  }
};
</script>

<style scoped>
.calculator-layer {
  position: fixed;
  inset: 0;
  z-index: 2400;
  pointer-events: none;
}

.scientific-calculator {
  position: fixed;
  top: 50%;
  left: 50%;
  width: min(430px, calc(100vw - 24px));
  max-height: calc(100vh - 16px);
  overflow: auto;
  transform: translate(-50%, -50%);
  border: 1px solid #b9c6e8;
  border-radius: 12px;
  background: #f4f6fc;
  box-shadow: 0 18px 48px rgba(19, 39, 91, 0.28), 0 2px 8px rgba(19, 39, 91, 0.16);
  color: #142552;
  pointer-events: auto;
  outline: none;
  font-family: Montserrat, sans-serif;
}

.calculator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 54px;
  padding: 0 14px 0 18px;
  border-radius: 11px 11px 0 0;
  background: #274aa8;
  color: #f7f9ff;
  cursor: move;
  user-select: none;
}

.calculator-header > div { display: flex; flex-direction: column; gap: 2px; }
.calculator-header strong { font-size: 15px; font-weight: 700; letter-spacing: 0.01em; }
.calculator-header span { color: #cfdaff; font-size: 10px; }

.calculator-close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #f7f9ff;
  cursor: pointer;
  font-size: 18px;
}
.calculator-close:hover, .calculator-close:focus-visible { background: rgba(255, 255, 255, 0.14); outline: none; }

.calculator-display {
  min-height: 96px;
  padding: 15px 18px 13px;
  border-bottom: 1px solid #d5dcef;
  background: #edf1fb;
  text-align: right;
}

.calculator-display-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 25px;
  color: #65739a;
  font-size: 12px;
}
.calculator-display-meta span { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.angle-mode {
  flex: none;
  padding: 4px 8px;
  border: 1px solid #aebce0;
  border-radius: 4px;
  background: #f8f9fd;
  color: #274aa8;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
}
.angle-mode:focus-visible { outline: 2px solid #4468ec; outline-offset: 1px; }

.calculator-result {
  min-height: 38px;
  overflow: hidden;
  color: #10204a;
  font-size: clamp(24px, 7vw, 32px);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.calculator-result.is-error { color: #b42318; font-size: 14px; line-height: 38px; }

.calculator-keypad {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 7px;
  padding: 12px;
}

.calculator-key {
  min-width: 0;
  height: 42px;
  padding: 0 4px;
  border: 1px solid #d2d9e9;
  border-radius: 6px;
  background: #fbfcff;
  color: #1f315f;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  transition: background-color 100ms ease, border-color 100ms ease, transform 100ms ease;
}
.calculator-key:hover { border-color: #9eadd2; background: #eef2fc; }
.calculator-key:active { transform: translateY(1px); }
.calculator-key:focus-visible { outline: 2px solid #4468ec; outline-offset: 1px; }
.calculator-key.is-number { background: #ffffff; color: #142552; font-size: 16px; }
.calculator-key.is-operator, .calculator-key.is-function, .calculator-key.is-constant, .calculator-key.is-postfix, .calculator-key.is-exponent, .calculator-key.is-open, .calculator-key.is-close { background: #e8edf9; }
.calculator-key.is-utility { background: #f2e9e7; border-color: #e1c5c0; color: #8e2d22; }
.calculator-key.is-equals { background: #3158c4; border-color: #3158c4; color: #f8faff; font-size: 19px; }
.calculator-key.is-equals:hover { background: #2448ad; }
.calculator-key.is-wide { grid-column: span 2; }
.calculator-key sup { font-size: 0.7em; }

@media (max-height: 580px) {
  .calculator-header { min-height: 46px; }
  .calculator-display { min-height: 76px; padding-top: 9px; padding-bottom: 8px; }
  .calculator-keypad { gap: 5px; padding: 8px; }
  .calculator-key { height: 34px; }
}

@media (prefers-reduced-motion: reduce) {
  .calculator-key { transition: none; }
}
</style>
